import React from 'react'
import PropTypes from 'prop-types'
import { useFormContext, Controller } from 'react-hook-form'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { usePickerActionsContext } from '@mui/x-date-pickers/hooks'
import { DesktopTimePicker } from '@mui/x-date-pickers/DesktopTimePicker'
import ListItem from '@mui/material/ListItem'

import { theme } from '../../themes/theme'
import { TextField } from '../styles/TextField'
import { InputWrapperDiv, FlexEndContainer } from '../styles/Containers'
import Button, { SecondaryButton } from '../common/Button'
import Label from './Label'

function ActionList(props) {
  const { className } = props
  const { acceptValueChanges, cancelValueChanges } = usePickerActionsContext()

  return (
    <FlexEndContainer className={className} padding="10px">
      <ListItem key="cancel" disablePadding>
        <SecondaryButton onClick={cancelValueChanges}>Cancel</SecondaryButton>
      </ListItem>
      <ListItem key="set" disablePadding>
        <Button onClick={acceptValueChanges}>Set</Button>
      </ListItem>
    </FlexEndContainer>
  )
}

ActionList.propTypes = {
  className: PropTypes.string
}

const CustomTextField = React.forwardRef((params, ref) => {
  const { required, ...rest } = params
  return (
    <TextField
      {...rest}
      inputRef={ref}
      variant="standard"
      InputLabelProps={{
        shrink: true,
        required,
        focused: false
      }}
    />
  )
})

CustomTextField.displayName = 'CustomTextField'

const getError = (id, errors) => {
  return id.split('.').reduce((o, i) => {
    const exists = o[i] !== undefined
    if (!exists) return {}
    return o[i]
  }, errors)
}

function TimeSelector({
  required = false,
  id,
  label,
  basis,
  defaultValue = null,
  props,
  inputProps,
  disabled = false,
  tooltip = 'Format example: 01:30 PM'
}) {
  const { control, formState } = useFormContext()

  const errors = formState.errors
  const error = getError(id, errors)

  return (
    <InputWrapperDiv basis={basis}>
      <Controller
        control={control}
        name={id}
        defaultValue={defaultValue}
        render={({ field: { onChange, value } }) => (
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DesktopTimePicker
              id={id}
              name={id}
              onChange={onChange}
              label={<Label label={label} tooltip={tooltip} />}
              value={value}
              defaultValue={defaultValue}
              enableAccessibleFieldDOMStructure={false}
              disabled={disabled}
              slots={{
                textField: CustomTextField,
                actionBar: ActionList
              }}
              slotProps={{
                textField: {
                  required,
                  haslabel: label === '' ? 'false' : 'true',
                  error: Object.keys(error).length > 0 && !!error,
                  helperText: error?.message,
                  FormHelperTextProps: { role: 'alert' },
                  sx: { '.MuiButtonBase-root.MuiIconButton-root': { marginRight: '0' } },
                  InputProps: {
                    disableUnderline: true,
                    spellCheck: false,
                    ...inputProps
                  }
                },
                popper: {
                  placement: 'bottom-end',
                  sx: {
                    '.MuiButtonBase-root.MuiMenuItem-root.MuiMultiSectionDigitalClockSection-item': {
                      fontFamily: theme.fonts.body,
                      fontSize: '14px'
                    },
                    '.MuiMultiSectionDigitalClock-root': { justifyContent: 'center' },
                    '.MuiMultiSectionDigitalClockSection-item.Mui-selected': { background: theme.colors.primary },
                    '.MuiMultiSectionDigitalClockSection-item.Mui-selected:hover': { background: theme.colors.primary },
                    '.MuiMultiSectionDigitalClockSection-item:hover': { background: theme.colors.primaryExtraLight },
                    '.MuiPickersLayout-contentWrapper': { gridColumn: '1 / 4' },
                    '.MuiList-root.MuiMultiSectionDigitalClockSection-root::after': { height: 'auto' }
                  }
                }
              }}
              {...props}
            />
          </LocalizationProvider>
        )}
      />
    </InputWrapperDiv>
  )
}

TimeSelector.propTypes = {
  id: PropTypes.string.isRequired,
  required: PropTypes.bool,
  label: PropTypes.string.isRequired,
  defaultValue: PropTypes.instanceOf(Date),
  disabled: PropTypes.bool,
  tooltip: PropTypes.string,
  basis: PropTypes.string,
  inputProps: PropTypes.object,
  props: PropTypes.object
}

export default TimeSelector
